import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useTranslations from 'hooks/useTranslations';

import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import HereLogo from '../Presentation/HereLogo';
import { TOP_BAR_HEIGHT } from '../TopBar/TopBar';
import { MenuWithArrowIcon } from '../../icons/common/MenuWithArrow';
import { MapIcon } from '../../icons/common/Map';
import { HistoryIcon } from '../../icons/common/History';
import { SettingsIcon } from '../../icons/common/Settings';
import { ROUTES } from '../../routes';
import { isMobileDevice } from '../../utils/helpers';

import { DRAWER_WIDTH, DRAWER_COLLAPSED_WIDTH, DRAWER_COLLAPSED_MOBILE_WIDTH } from './constants';

const SideBarList = styled(List)(() => ({
  padding: '14px 8px 12px',
  '@media screen and (max-width: 850px)': {
    padding: '8px 4px 8px',
  },
}));

const SideBarHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: TOP_BAR_HEIGHT,
  padding: '0 16px',
}));

const SideBarListItem = styled(ListItem)(() => ({
  padding: '0',
  // borderRight: '4px solid transparent',
  '&:not(:last-child)': {
    marginBottom: '4px',
  },
}));

const SideBarListItemButton = styled(ListItemButton)(() => ({
  padding: '0 16px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  borderRadius: '8px',
  '&.collapsed': {
    padding: '0',
    justifyContent: 'center',
  },
  '&.active': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    '& svg': {
      stroke: '#7ad7d2',
    },
  },
  '@media screen and (max-width: 850px)': {
    height: '36px',
    '&.collapsed': {
      padding: '0',
      justifyContent: 'center',
    },
  },
}));

const SideBarListIcon = styled('div')(() => ({
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0',
  '@media screen and (max-width: 850px)': {
    width: '36px',
    height: '36px',
    '& svg': {
      width: '20px',
      height: '20px',
    },
  },
}));

const SideBarListItemText = styled('div')(() => ({
  color: '#1a2f5a',
  whiteSpace: 'nowrap',
  fontSize: '.875rem',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 0 0 8px',
}));

type SideBarProps = {
  collapsed: boolean;
  onClickMenu: () => void;
};

const SideBar = ({ collapsed, onClickMenu }: SideBarProps) => {
  const translations = useTranslations();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const topMenu = [
    {
      key: 'tour-planner',
      icon: <MapIcon />,
      label: translations?.sideBar?.tourPlannerLabel ?? 'Tour Planner',
      route: ROUTES.tourPlanner,
    },
    {
      key: 'previous-sessions',
      icon: <HistoryIcon />,
      label: translations?.sideBar?.previousSessionsLabel ?? 'Previous Sessions',
      route: ROUTES.sessions,
    },
    {
      key: 'settings',
      icon: <SettingsIcon />,
      label: translations?.sideBar?.settingsLabel ?? 'Settings',
      route: ROUTES.settings,
    },
  ];

  const isActive = (route: string) => {
    if (route === ROUTES.tourPlanner) {
      return pathname === '/' || pathname === ROUTES.tourPlanner;
    }
    return pathname.startsWith(route);
  };

  const isMobile = isMobileDevice();
  const collapsedWidth = isMobile ? DRAWER_COLLAPSED_MOBILE_WIDTH : DRAWER_COLLAPSED_WIDTH;

  return (
    <Drawer
      className="left-side-bar"
      variant="persistent"
      open
      sx={{
        width: collapsed ? collapsedWidth : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? collapsedWidth : DRAWER_WIDTH,
          overflowX: 'hidden',
          transition: 'width 0.2s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <SideBarHeader>
        <HereLogo data-test-id="HereLogoOnSideBar" />
      </SideBarHeader>
      <Divider />
      <SideBarList className="top-side-bar-menu">
        {topMenu.map((item) => (
          <SideBarListItem key={item.key}>
            <SideBarListItemButton
              className={`${isActive(item.route) ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`}
              onClick={() => navigate(item.route + search)}
            >
              <SideBarListIcon>{item.icon}</SideBarListIcon>
              {!collapsed && <SideBarListItemText>{item.label}</SideBarListItemText>}
            </SideBarListItemButton>
          </SideBarListItem>
        ))}
      </SideBarList>
      <Box sx={{ flexGrow: 1 }} />
      <SideBarList className="bottom-side-bar-menu">
        <SideBarListItem key="side-bar-toggle-drawer">
          <SideBarListItemButton onClick={onClickMenu}>
            <SideBarListIcon>
              <Box
                sx={{
                  display: 'flex',
                  transform: collapsed ? 'rotate(180deg)' : 'none',
                  height: '40px',
                  alignItems: 'center',
                }}
              >
                <MenuWithArrowIcon />
              </Box>
            </SideBarListIcon>
            {!collapsed && (
              <SideBarListItemText
                sx={{
                  fontWeight: '600',
                }}
              >
                {translations?.sideBar?.hideMenuLabel ?? 'Hide menu'}
              </SideBarListItemText>
            )}
          </SideBarListItemButton>
        </SideBarListItem>
      </SideBarList>
    </Drawer>
  );
};

export default SideBar;
